/**
 * Thin fetch wrapper for the Worker API. In production, Cloudflare Access
 * sits in front of the app and attaches its own session cookie automatically
 * (credentials: "include"); no token handling is needed client-side. The
 * Worker verifies identity server-side from the Access JWT header — this
 * client never carries or trusts any auth token itself (spec §17).
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

/**
 * Base URL the Worker API is reached at. When the frontend and Worker are
 * deployed to the same origin (e.g. both behind one custom domain with
 * path-based routing — see DEPLOYMENT.md), this is left unset and requests
 * stay relative. When deployed separately (Cloudflare Pages + a Worker on
 * its own subdomain, this build's default), VITE_API_BASE_URL must be set
 * at build time to the Worker's absolute URL so requests don't get sent to
 * the Pages origin itself.
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}/api${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = undefined;
    }
    const hasMessage = body && typeof body === "object" && "message" in body;
    const message = hasMessage
      ? String((body as { message: unknown }).message)
      : `Request failed with status ${res.status}`;
    throw new ApiError(message, res.status, body);
  }
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) => request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
